import type { NodeType, StoryCtx, StoryNode } from '@dispositio/core';
import { childrenOf, displayOf } from '@dispositio/core';

/** Symbol, das ein Kind ohne Titelbild in der Vorschau bekommt. */
export type Glyph = 'person' | 'place' | 'object' | 'text' | 'event' | 'period' | 'node';

/** Ein Kind, wie es in der Inhaltsvorschau eines Nodes erscheint. */
export interface Kid {
  id: string;
  title: string;
  /** Hover-Nachricht und Klassenname (Tooltip über der Kachel) */
  hover: string;
  typeName: string;
  typeId: string;
  typeColor: string;
  tagColor: string | null;
  imageId: string | null;
  glyph: Glyph;
}

export function glyphOf(type: NodeType): Glyph {
  if (type.builtin && type.name === 'Person') return 'person';
  switch (type.category) {
    case 'place':
      return 'place';
    case 'object':
      return 'object';
    case 'event':
      return 'event';
    case 'period':
      return 'period';
    case 'normal':
      return type.textual ? 'text' : 'node';
    default:
      return 'node';
  }
}

/** Reihenfolge der Gruppen in der Vorschau: erst Beschreibungen/normale Nodes, dann Personen/Objekte, dann Orte, dann Zeitelemente. */
const CATEGORY_RANK: Record<string, number> = { normal: 0, object: 1, place: 2, event: 3, period: 4 };

/** Alle Nachfahren eines Nodes (Kinder, deren Kinder usw.) in Baumreihenfolge; Zeitstrahle selbst nicht, ihre Elemente schon. */
function descendantsOf(ctx: StoryCtx, node: StoryNode): StoryNode[] {
  const out: StoryNode[] = [];
  const visit = (n: StoryNode): void => {
    for (const k of childrenOf(ctx, n.id)) {
      // Zeitstrahle selbst erscheinen nicht, wohl aber was an ihnen liegt (z. B. an der inneren Zeitleiste eines Zeitraums)
      if (k.kind !== 'timeline') out.push(k);
      visit(k);
    }
  };
  visit(node);
  return out;
}

/**
 * Inhalt eines Nodes für die Vorschau: **alle** Nachfahren (Kinder, Kinder der Kinder, …), nach Klasse gruppiert
 * (Klassenart, Klassenreihenfolge, dann Baumreihenfolge).
 */
export function kidsOf(ctx: StoryCtx, node: StoryNode): Kid[] {
  const kids = descendantsOf(ctx, node).map((k) => {
    const d = displayOf(ctx, k);
    const type = ctx.types.get(k.typeId)!;
    return { sort: (CATEGORY_RANK[type.category] ?? 9) * 1000 + type.sort, kid: { id: k.id, title: d.title, hover: d.hover, typeName: d.typeName, typeId: k.typeId, typeColor: d.typeColor, tagColor: d.tagColor, imageId: d.imageId, glyph: glyphOf(type) } satisfies Kid };
  });
  return kids.sort((a, b) => a.sort - b.sort).map((x) => x.kid);
}

/** Zoomstufe der Vorschau: 0 Punkte, 1 kleine Vorschaubilder, 2 Symbole, die den freien Platz nutzen. */
export type ContentLevel = 0 | 1 | 2;

/** Kantenlänge der Kacheln, Abstand und Höhe der Beschriftung je Stufe (Modellraum). */
export const CELL = {
  0: { size: 26, gap: 6, label: 0 },
  1: { size: 52, gap: 8, label: 0 },
  2: { size: 72, gap: 10, label: 18 },
} as const;
export const MAX_STRIP_WIDTH = 900;

export interface Placed {
  kid: Kid;
  /** linke obere Ecke der Kachel im Streifen */
  x: number;
  y: number;
}

export interface ContentLayout {
  level: ContentLevel;
  /** Größe des ganzen Streifens */
  w: number;
  h: number;
  items: Placed[];
}

/**
 * Ordnet die Kinder in Zeilen an, die in `maxWidth` passen. Stufe 0 und 1 fließen als eine Folge; Stufe 2 beginnt für jede Klasse
 * eine neue Zeile (wie im Entwurf: erst alle Beschreibungen, darunter die Personen, dann die Orte). Gibt null für leeren Inhalt.
 */
export function layoutContent(kids: Kid[], level: ContentLevel, maxWidth: number): ContentLayout | null {
  if (kids.length === 0) return null;
  const { size, gap, label } = CELL[level];
  const step = size + gap;
  const width = Math.min(Math.max(maxWidth, 4 * step - gap), MAX_STRIP_WIDTH);
  const perRow = Math.max(1, Math.floor((width + gap) / step));
  const rowH = size + label;

  const items: Placed[] = [];
  let col = 0;
  let row = 0;
  let lastType: string | null = null;
  let usedCols = 0;
  const place = (): { x: number; y: number } => {
    const p = { x: col * step, y: row * (rowH + gap) };
    col++;
    usedCols = Math.max(usedCols, col);
    if (col >= perRow) {
      col = 0;
      row++;
    }
    return p;
  };
  for (const kid of kids) {
    if (level === 2 && lastType !== null && kid.typeId !== lastType && col !== 0) {
      col = 0;
      row++;
    }
    lastType = kid.typeId;
    items.push({ kid, ...place() });
  }
  const rows = col === 0 ? row : row + 1;
  return { level, w: usedCols * step - gap, h: rows * rowH + (rows - 1) * gap, items };
}
